import { useEffect } from "react";
import { useLocation } from "wouter";

import { SITE_NAME, SITE_ORIGIN, ROUTE_METADATA, type RouteMetadata, buildRouteJsonLd } from "@shared/site-metadata";
const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/og-image.png`;

const NOT_FOUND_METADATA: RouteMetadata = {
  title: "Page Not Found | F1 Tracker",
  description: "The page you're looking for doesn't exist.",
};

function setMeta(selector: string, attr: string, value: string) {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    const [, attrName, attrValue] = selector.match(/\[(\w+)="([^"]+)"\]/) ?? [];
    if (attrName && attrValue) el.setAttribute(attrName, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

function setLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

const ROUTE_JSONLD_ID = "route-jsonld";

function setRouteJsonLd(payload: unknown) {
  let el = document.head.querySelector<HTMLScriptElement>(`script#${ROUTE_JSONLD_ID}`);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = ROUTE_JSONLD_ID;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(payload);
}

export const usePageMetadata = () => {
  const [location] = useLocation();

  useEffect(() => {
    const meta = ROUTE_METADATA[location] ?? NOT_FOUND_METADATA;
    const url = `${SITE_ORIGIN}${location}`;

    document.title = meta.title;
    setMeta('meta[name="robots"]', "content", ROUTE_METADATA[location] ? "index, follow, max-image-preview:large" : "noindex, follow");
    setMeta('meta[name="description"]', "content", meta.description);
    setLink("canonical", url);
    setMeta('meta[property="og:title"]', "content", meta.title);
    setMeta('meta[property="og:description"]', "content", meta.description);
    setMeta('meta[property="og:url"]', "content", url);
    setMeta('meta[property="og:image"]', "content", meta.image || DEFAULT_OG_IMAGE);
    setMeta('meta[property="og:site_name"]', "content", SITE_NAME);
    setMeta('meta[name="twitter:title"]', "content", meta.title);
    setMeta('meta[name="twitter:description"]', "content", meta.description);
    setMeta('meta[name="twitter:image"]', "content", meta.image || DEFAULT_OG_IMAGE);

    setRouteJsonLd(buildRouteJsonLd(location, meta));
  }, [location]);
};
